/**
 * Aba Autonomia em edição (Item 368 F2).
 *
 * Edita `autonomyPhase` (manual|proposal|autonomous) com risk-gate por vertical:
 * verticals 'retention' | 'collections' | 'legal' bloqueiam proposal/autonomous
 * (o backend rejeita com 400 — a UI antecipa o gate, sem fazer requisição inútil).
 *
 * O campo `defaultOperationMode` (H_a..H_d) é editado pela aba Geral e não
 * aparece aqui — a aba Autonomia se concentra na escada de graduação da IA.
 */

import { useCapability } from '../../../hooks/use-capability';
import {
  AUTONOMY_PHASE_LABELS,
  BAND_LABELS,
  NATIVE_SCHEDULING_MODE_OPTIONS,
  RISK_VERTICALS_FE,
  VALIDATION_MODE_LABELS,
  WAITLIST_PROMOTION_MODE_OPTIONS,
} from '../playbook-labels';
import { useProposalMetrics } from '../use-playbooks';

import type { PlaybookAutonomyPhase, PlaybookDto, PlaybookValidationMode } from '../types';
import type { PlaybookForm } from '../use-playbook-form';

const PHASE_ORDER: PlaybookAutonomyPhase[] = ['manual', 'proposal', 'autonomous'];
const VALIDATION_ORDER: PlaybookValidationMode[] = ['none', 'ai_optional', 'ai_required'];

export function TabOperationEdit({
  form,
  playbook,
}: {
  form: PlaybookForm;
  playbook: PlaybookDto;
}): JSX.Element {
  const { draft, set } = form;
  const isRiskVertical = RISK_VERTICALS_FE.has(playbook.vertical);
  const { data: metrics } = useProposalMetrics(playbook.id);
  const canNative = useCapability('scheduling-native');
  const canWaitlist = useCapability('scheduling-waitlist');

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Autonomia">
      <h2 className="pb-panel__title">Autonomia</h2>

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Fase de autonomia da IA</span>
        <div className="pb-radio-row" role="radiogroup" aria-label="Fase de autonomia">
          {PHASE_ORDER.map((phase) => {
            const meta = AUTONOMY_PHASE_LABELS[phase];
            const selected = phase === draft.autonomyPhase;
            const blocked = isRiskVertical && phase !== 'manual';
            return (
              <button
                key={phase}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={blocked}
                className={
                  'pb-radio pb-radio--editable' +
                  (selected ? ' pb-radio--selected' : '') +
                  (blocked ? ' pb-radio--disabled' : '')
                }
                onClick={() => {
                  if (!blocked) set('autonomyPhase', phase);
                }}
              >
                <span className="pb-radio__dot" />
                <span className="pb-radio__main">
                  <span className="pb-radio__label">{meta.label}</span>
                  <span className="pb-radio__desc">{meta.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
        {isRiskVertical && (
          <p className="pb-panel__hint pb-panel__hint--warn">
            Vertical de risco — sempre manual (retention / collections / legal não permitem proposta
            nem autonomia plena).
          </p>
        )}
      </div>

      <div className="pb-panel__divider" />

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">
          Aceitação da IA
          {metrics != null && metrics.band !== 'none'
            ? ` · últimos ${metrics.windowDays} dias`
            : ''}
        </span>
        {metrics == null || metrics.band === 'none' ? (
          <p className="pb-panel__hint">Ainda sem propostas de condução neste playbook.</p>
        ) : (
          <div className="pb-autonomy-metrics">
            <p className="pb-autonomy-metrics__headline">
              <span>
                {metrics.granted} de {metrics.resolvedCount} resolvidas aceitas
              </span>
              {metrics.acceptanceRate !== null && (
                <>
                  {' · '}
                  <strong className="pb-autonomy-metrics__rate">
                    {Math.round(metrics.acceptanceRate * 100)}%
                  </strong>
                </>
              )}
              {' · '}
              {metrics.band === 'insufficient' ? (
                <span className="pb-autonomy-metrics__band">amostra pequena</span>
              ) : (
                <span
                  className={`pb-autonomy-metrics__band pb-autonomy-metrics__band--${metrics.band}`}
                >
                  {BAND_LABELS[metrics.band]}
                </span>
              )}
            </p>
            {/* `expired` só aparece quando > 0 — a transição pending→expired
                depende do worker de expiração (diferido na Fatia B); mostrar
                "0 expiraram" enganaria. */}
            <p className="pb-panel__hint">
              {metrics.expired > 0 && (
                <>
                  {metrics.expired} expirada{metrics.expired === 1 ? '' : 's'} sem decisão{' · '}
                </>
              )}
              {metrics.pending} pendente{metrics.pending === 1 ? '' : 's'}
            </p>
          </div>
        )}
      </div>

      <div className="pb-panel__divider" />

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Modo de validação</span>
        <div className="pb-radio-row" role="radiogroup" aria-label="Modo de validação">
          {VALIDATION_ORDER.map((mode) => {
            const meta = VALIDATION_MODE_LABELS[mode];
            const selected = mode === draft.validationMode;
            return (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={selected}
                className={'pb-radio pb-radio--editable' + (selected ? ' pb-radio--selected' : '')}
                onClick={() => {
                  set('validationMode', mode);
                }}
              >
                <span className="pb-radio__dot" />
                <span className="pb-radio__main">
                  <span className="pb-radio__label">{meta.label}</span>
                  <span className="pb-radio__desc">{meta.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Frente B Plano 3 (B2) — override de agenda nativa por-playbook (gated). */}
      {canNative && (
        <>
          <div className="pb-panel__divider" />
          <div className="pb-panel__section">
            <span className="pb-panel__section-label">Agenda nativa — modo de exposição</span>
            <p className="pb-panel__hint">
              Como a IA conduz o agendamento neste playbook. "Herdar" usa o padrão do tenant; um
              cliente específico ainda pode sobrepor este playbook.
            </p>
            <div className="pb-radio-row" role="radiogroup" aria-label="Modo de agenda do playbook">
              {NATIVE_SCHEDULING_MODE_OPTIONS.map((opt) => {
                const selected = draft.nativeSchedulingMode === opt.value;
                return (
                  <button
                    key={opt.value ?? 'inherit'}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={'pb-radio pb-radio--editable' + (selected ? ' pb-radio--selected' : '')}
                    onClick={() => {
                      set('nativeSchedulingMode', opt.value);
                    }}
                  >
                    <span className="pb-radio__dot" />
                    <span className="pb-radio__main">
                      <span className="pb-radio__label">{opt.label}</span>
                      <span className="pb-radio__desc">{opt.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {canWaitlist && (
            <div className="pb-panel__section">
              <span className="pb-panel__section-label">Fila de espera — modo de promoção</span>
              <p className="pb-panel__hint">
                Como a fila é oferecida quando um horário abre, neste playbook. "Herdar" usa a
                política do tenant.
              </p>
              <div
                className="pb-radio-row"
                role="radiogroup"
                aria-label="Modo de promoção da fila do playbook"
              >
                {WAITLIST_PROMOTION_MODE_OPTIONS.map((opt) => {
                  const selected = draft.waitlistPromotionMode === opt.value;
                  return (
                    <button
                      key={opt.value ?? 'inherit'}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={
                        'pb-radio pb-radio--editable' + (selected ? ' pb-radio--selected' : '')
                      }
                      onClick={() => {
                        set('waitlistPromotionMode', opt.value);
                      }}
                    >
                      <span className="pb-radio__dot" />
                      <span className="pb-radio__main">
                        <span className="pb-radio__label">{opt.label}</span>
                        <span className="pb-radio__desc">{opt.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
